/**
 * React Bits — AnimatedList, adapted from
 * https://reactbits.dev/components/animated-list
 *
 * Trimmed to the one thing this dashboard needs: each child fades in and
 * rises 6px, staggered by `stagger` seconds. No scroll container, no keyboard
 * selection, no gradient masks. Static when the user prefers reduced motion.
 */

import { motion } from 'motion/react';
import { Children, type ReactNode } from 'react';

interface AnimatedListProps {
  children: ReactNode;
  /** Seconds between consecutive children. Default 0.04. */
  stagger?: number;
  /** Seconds before the first child starts. */
  delay?: number;
  duration?: number;
  distance?: number;
  className?: string;
  /** Applied to the wrapper around each child. */
  itemClassName?: string;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function AnimatedList({
  children,
  stagger = 0.04,
  delay = 0,
  duration = 0.3,
  distance = 6,
  className,
  itemClassName,
}: AnimatedListProps) {
  const reduce = prefersReducedMotion();
  const items = Children.toArray(children);

  return (
    <div className={className}>
      {items.map((child, i) => (
        <motion.span
          key={i}
          className={itemClassName}
          style={{ display: 'inline-block' }}
          initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: distance }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: reduce ? 0 : duration,
            delay: reduce ? 0 : delay + i * stagger,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          {child}
        </motion.span>
      ))}
    </div>
  );
}

export default AnimatedList;
